<script lang="ts">
  // Breakthrough · Poster: a full-bleed photo with the biggest signature gain as the headline.
  import { MEDAL_COLORS, type ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: bt = ctx.data.breakthrough!
  $: medal = MEDAL_COLORS[bt.level]
  $: stats = statsFor(ctx, ['stat.xss', 'stat.time', 'stat.distance', 'stat.difficulty', 'stat.recovery'])

  // Shown metrics, biggest relative gain first; that one is the headline.
  $: metrics = (() => {
    const before = bt.before
    const after = bt.after
    const delta = bt.delta
    if (!before || !after || !delta) return []
    const list = [
      { key: 'delta.tp', label: 'Threshold Power', delta: delta.tp, before: ctx.fmt.int(before.tp), after: ctx.fmt.int(after.tp), unit: 'W', digits: 0, rel: delta.tp / (before.tp || 1) },
      { key: 'delta.hie', label: 'High Intensity Energy', delta: delta.hie, before: ctx.fmt.fixed(before.hie, 1), after: ctx.fmt.fixed(after.hie, 1), unit: 'kJ', digits: 1, rel: delta.hie / (before.hie || 1) },
      { key: 'delta.pp', label: 'Peak Power', delta: delta.pp, before: ctx.fmt.int(before.pp), after: ctx.fmt.int(after.pp), unit: 'W', digits: 0, rel: delta.pp / (before.pp || 1) },
    ]
    return list.filter((m) => ctx.show(m.key)).sort((a, b) => b.rel - a.rel)
  })()
  $: hero = metrics[0]
  $: chartHeight = { portrait: 110, square: 84, landscape: 96 }[ctx.size]
  $: rest = metrics.slice(1)
</script>

<Frame {ctx} shade="bottom" base="linear-gradient(180deg, #24343c 0%, #0b1114 100%)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-ribbon" style="background: {medal.main};">
        <Icon name="award" size={34} stroke={2.6} color="#11181c" />
        {bt.level === 'near' ? 'Near' : medal.label} Breakthrough
      </span>
      <Brand height={40} />
    </div>

    {#if hero}
      <div class="xr-hero">
        <div class="xr-hero-value">
          <span class="xr-num">{ctx.fmt.signed(hero.delta, hero.digits)}</span><span class="xr-hero-unit">{hero.unit}</span>
        </div>
        <div class="xr-hero-label">{hero.label}</div>
        <div class="xr-hero-sub">{hero.before} → <strong>{hero.after}</strong> {hero.unit}</div>
      </div>
      {#if rest.length}
        <div class="xr-rest">
          {#each rest as m}
            <div class="xr-rest-item">
              <span class="xr-rest-value">{ctx.fmt.signed(m.delta, m.digits)} {m.unit}</span>
              <span class="xr-rest-label">{m.label}</span>
            </div>
          {/each}
        </div>
      {/if}
    {/if}

    {#if ctx.show('chart') && ctx.data.series?.power}
      <div class="xr-chart">
        <Fill minHeight={chartHeight} let:width>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} height={chartHeight} breakthrough={bt.level} showMpa={false} fillOpacity={0.3} color="#ffffff" baseline={false} />
        </Fill>
      </div>
    {/if}

    <div class="xr-footer">
      <div class="xr-footer-title"><TitleBlock {ctx} size="md" lines={1} /></div>
      {#if stats.length}<div class="xr-footer-stats"><StatGrid {stats} columns={4} size="sm" /></div>{/if}
      <div class="xr-footer-badges"><Badges {ctx} own="breakthrough" /></div>
    </div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto auto auto auto;
    grid-template-areas: 'top' '.' 'hero' 'rest' 'chart' 'footer';
  }
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top top' '. .' 'hero rest' 'chart chart' 'footer footer';
    column-gap: 60px;
  }
  .xr-top { grid-area: top; }
  .xr-hero { grid-area: hero; min-width: 0; }
  .xr-rest { grid-area: rest; }
  .xr-chart { grid-area: chart; display: flex; }
  .xr-footer { grid-area: footer; }
  .xr-footer-badges:empty { display: none; }
  .xr-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .xr-ribbon {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 14px 28px 14px 20px;
    border-radius: 999px;
    color: #11181c;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  }
  .xr-hero {
    text-shadow: 0 4px 30px rgba(0, 0, 0, 0.45);
  }
  .xr-hero-value {
    display: flex;
    align-items: baseline;
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    line-height: 0.82;
  }
  .xr-num {
    font-size: 300px;
    letter-spacing: -0.01em;
  }
  :global(.xr-square) .xr-num {
    font-size: 230px;
  }
  :global(.xr-landscape) .xr-num {
    font-size: 280px;
  }
  .xr-hero-unit {
    font-size: 110px;
    margin-left: 16px;
    color: var(--accent);
  }
  :global(.xr-square) .xr-hero-unit {
    font-size: 88px;
  }
  .xr-hero-label {
    margin-top: 14px;
    font-family: var(--display);
    font-weight: 700;
    font-size: 64px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .xr-hero-sub {
    margin-top: 6px;
    font-size: 30px;
    color: var(--muted);
  }
  .xr-hero-sub strong {
    color: #fff;
    font-weight: 700;
  }
  .xr-rest {
    display: flex;
    gap: 48px;
    margin-top: 28px;
  }
  :global(.xr-square) .xr-rest {
    margin-top: 22px;
  }
  :global(.xr-landscape) .xr-rest {
    flex-direction: column;
    justify-content: flex-end;
    gap: 30px;
    margin: 0 0 8px;
  }
  .xr-rest-item {
    display: flex;
    flex-direction: column;
    padding-left: 20px;
    border-left: 5px solid var(--accent);
  }
  .xr-rest-value {
    font-family: var(--display);
    font-weight: 700;
    font-size: 56px;
    line-height: 1;
  }
  .xr-rest-label {
    margin-top: 4px;
    font-size: 22px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-chart {
    margin-top: 34px;
  }
  :global(.xr-square) .xr-chart {
    margin-top: 24px;
  }
  .xr-footer {
    display: flex;
    flex-direction: column;
    gap: 26px;
    margin-top: 34px;
    padding-top: 30px;
    border-top: 2px solid var(--faint);
  }
  :global(.xr-square) .xr-footer {
    gap: 20px;
    margin-top: 24px;
    padding-top: 22px;
  }
  /* Landscape: title and numbers side by side, badges under. */
  :global(.xr-landscape) .xr-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
    grid-template-areas: 'title stats' 'badges badges';
    column-gap: 60px;
    row-gap: 22px;
    align-items: end;
  }
  :global(.xr-landscape) .xr-footer-title { grid-area: title; min-width: 0; }
  :global(.xr-landscape) .xr-footer-stats { grid-area: stats; }
  :global(.xr-landscape) .xr-footer-badges { grid-area: badges; }
</style>
